import { Image, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";

const grainTexture =
  require("@src/apps/moon/assets/images/gradient-grain-texture-2.jpg") as number;

type Tone =
  "cream" | "amber" | "copper" | "brown" | "frost" | "violet" | "blue";

export type ProgramCardIllustrationKind =
  | "fertility-check-in"
  | "genetic-screening"
  | "full-assessment"
  | "her-optimization"
  | "his-optimization"
  | "couples-optimization"
  | "egg-freezing"
  | "embryo-freezing"
  | "iui-cycle"
  | "ivf-cycle"
  | "fet-cycle"
  | "generic";

const TONES: Record<Tone, [string, string, string]> = {
  cream: ["#FFF8EC", "#F1D1A6", "#B9784C"],
  amber: ["#FFE8A5", "#F4B34F", "#9C5528"],
  copper: ["#F2B387", "#B76545", "#542F27"],
  brown: ["#9C604D", "#5B332C", "#2F1D19"],
  frost: ["#FFFDFC", "#F3E5CF", "#B88C68"],
  violet: ["#F4E8FF", "#B78ACE", "#6F4A87"],
  blue: ["#EAF2FF", "#8BA0CB", "#4E638F"],
};

function Shape({
  x,
  y,
  w,
  h = w,
  tone,
  opacity = 1,
  radius,
  rotate = 0,
}: {
  x: number;
  y: number;
  w: number;
  h?: number;
  tone: Tone;
  opacity?: number;
  radius?: number;
  rotate?: number;
}) {
  return (
    <View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        borderRadius: radius ?? Math.max(w, h),
        opacity,
        overflow: "hidden",
        transform: [{ rotate: `${rotate}deg` }],
      }}
    >
      <LinearGradient
        colors={TONES[tone]}
        start={{ x: 0.12, y: 0.08 }}
        end={{ x: 0.92, y: 1 }}
        style={{ flex: 1 }}
      />
      <Image
        source={grainTexture}
        resizeMode="cover"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          opacity: 0.16,
        }}
      />
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: w * 0.22,
          top: h * 0.16,
          width: w * 0.28,
          height: h * 0.22,
          borderRadius: Math.max(w, h),
          backgroundColor: "rgba(255,255,255,0.38)",
        }}
      />
    </View>
  );
}

function Line({
  x,
  y,
  w,
  rotate = 0,
  color = "rgba(92,52,32,0.34)",
}: {
  x: number;
  y: number;
  w: number;
  rotate?: number;
  color?: string;
}) {
  return (
    <View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: 1.2,
        borderRadius: 999,
        backgroundColor: color,
        transform: [{ rotate: `${rotate}deg` }],
      }}
    />
  );
}

function FertilityCheckIn() {
  return (
    <>
      <Shape x={7} y={13} w={34} h={27} tone="frost" radius={11} />
      <Shape x={12} y={18} w={8} tone="amber" />
      <Shape x={25} y={19} w={9} tone="brown" opacity={0.88} />
      <Line x={13} y={33} w={20} rotate={-12} />
      <Line x={16} y={29} w={8} rotate={-42} color="rgba(154,77,25,0.42)" />
      <Line x={25} y={27} w={8} rotate={30} color="rgba(154,77,25,0.42)" />
    </>
  );
}

function GeneticScreening() {
  return (
    <>
      <Line x={16} y={14} w={22} rotate={58} />
      <Line x={13} y={35} w={22} rotate={-58} />
      <Shape x={12} y={10} w={8} tone="violet" />
      <Shape x={28} y={13} w={7} tone="blue" />
      <Shape x={18} y={21} w={8} tone="copper" />
      <Shape x={29} y={27} w={8} tone="violet" />
      <Shape x={11} y={32} w={7} tone="blue" />
      <Line x={17} y={18} w={13} rotate={10} color="rgba(114,82,143,0.32)" />
      <Line x={19} y={29} w={12} rotate={-8} color="rgba(78,99,143,0.34)" />
    </>
  );
}

function FullAssessment() {
  return (
    <>
      <Shape x={8} y={12} w={31} h={25} tone="cream" radius={8} rotate={-4} />
      <Shape x={12} y={9} w={30} h={25} tone="frost" radius={8} rotate={5} />
      <Shape x={16} y={17} w={6} tone="amber" />
      <Shape x={25} y={16} w={6} tone="copper" />
      <Shape x={33} y={17} w={5} tone="brown" opacity={0.82} />
      <Line x={17} y={31} w={18} rotate={-8} />
      <Line x={18} y={26} w={8} rotate={-36} color="rgba(154,77,25,0.36)" />
    </>
  );
}

function HerOptimization() {
  return (
    <>
      <Shape x={12} y={13} w={24} h={30} tone="cream" radius={18} />
      <Shape x={26} y={9} w={13} h={20} tone="violet" radius={11} rotate={34} />
      <Shape x={8} y={28} w={10} tone="amber" opacity={0.82} />
      <Line x={30} y={19} w={11} rotate={-30} color="rgba(114,82,143,0.36)" />
      <Shape x={21} y={23} w={7} tone="copper" opacity={0.82} />
    </>
  );
}

function HisOptimization() {
  return (
    <>
      <Line x={11} y={18} w={27} rotate={26} color="rgba(78,99,143,0.32)" />
      <Line x={13} y={27} w={24} rotate={-16} color="rgba(78,99,143,0.28)" />
      <Shape x={27} y={17} w={12} h={9} tone="blue" radius={8} />
      <Line x={12} y={20} w={18} rotate={20} color="rgba(78,99,143,0.44)" />
      <Shape x={15} y={31} w={7} tone="amber" opacity={0.72} />
      <Shape x={35} y={29} w={6} tone="copper" opacity={0.78} />
    </>
  );
}

function CouplesOptimization() {
  return (
    <>
      <Shape x={8} y={13} w={22} h={28} tone="violet" radius={16} />
      <Shape x={22} y={12} w={22} h={28} tone="blue" radius={16} />
      <Shape x={18} y={19} w={15} h={17} tone="amber" opacity={0.78} />
      <Line x={14} y={34} w={26} rotate={-3} />
      <Shape x={6} y={28} w={6} tone="cream" opacity={0.86} />
      <Shape x={41} y={26} w={6} tone="cream" opacity={0.86} />
    </>
  );
}

function EggFreezing() {
  return (
    <>
      <Shape x={9} y={21} w={13} h={17} tone="cream" radius={10} />
      <Shape x={20} y={15} w={14} h={18} tone="frost" radius={10} />
      <Shape x={31} y={23} w={12} h={16} tone="cream" radius={10} />
      <Line x={11} y={12} w={9} rotate={45} color="rgba(78,99,143,0.36)" />
      <Line x={15} y={12} w={9} rotate={-45} color="rgba(78,99,143,0.36)" />
      <Line x={36} y={12} w={9} rotate={45} color="rgba(78,99,143,0.36)" />
      <Line x={40} y={12} w={9} rotate={-45} color="rgba(78,99,143,0.36)" />
      <Shape x={26} y={35} w={7} tone="blue" opacity={0.7} />
    </>
  );
}

function EmbryoFreezing() {
  return (
    <>
      <Shape x={15} y={16} w={22} h={22} tone="frost" radius={14} />
      <Shape x={17} y={18} w={9} tone="cream" />
      <Shape x={26} y={18} w={9} tone="amber" />
      <Shape x={19} y={27} w={8} tone="copper" opacity={0.86} />
      <Shape x={28} y={27} w={7} tone="violet" opacity={0.8} />
      <Line x={5} y={32} w={17} rotate={-18} color="rgba(78,99,143,0.32)" />
      <Shape x={5} y={28} w={7} h={5} tone="blue" radius={5} />
      <Line x={35} y={12} w={13} rotate={90} color="rgba(78,99,143,0.34)" />
    </>
  );
}

function IuiCycle() {
  return (
    <>
      <Shape x={23} y={14} w={19} h={25} tone="cream" radius={14} />
      <Shape x={28} y={20} w={9} tone="amber" />
      <Line x={7} y={28} w={29} rotate={-8} color="rgba(154,77,25,0.42)" />
      <Shape x={7} y={25} w={8} h={5} tone="copper" radius={5} />
      <Line x={34} y={12} w={9} rotate={36} color="rgba(154,77,25,0.28)" />
      <Line x={36} y={38} w={8} rotate={-34} color="rgba(154,77,25,0.24)" />
    </>
  );
}

function IvfCycle() {
  return (
    <>
      <Shape x={8} y={19} w={32} h={22} tone="frost" radius={16} />
      <Shape x={16} y={24} w={7} tone="amber" />
      <Shape x={25} y={25} w={8} tone="cream" />
      <Shape x={32} y={29} w={5} tone="copper" opacity={0.82} />
      <Shape x={29} y={7} w={6} h={22} tone="brown" radius={4} rotate={42} />
      <Shape x={24} y={15} w={9} h={5} tone="blue" radius={5} rotate={42} />
    </>
  );
}

function FetCycle() {
  return (
    <>
      <Shape x={11} y={13} w={14} h={24} tone="blue" radius={7} rotate={-8} />
      <Shape x={17} y={27} w={23} h={12} tone="frost" radius={10} />
      <Shape x={23} y={24} w={7} tone="cream" />
      <Shape x={30} y={25} w={7} tone="amber" />
      <Line x={30} y={18} w={16} rotate={28} color="rgba(154,77,25,0.32)" />
      <Shape x={40} y={20} w={6} tone="copper" opacity={0.8} />
    </>
  );
}

function GenericProgram() {
  return (
    <>
      <Shape x={8} y={18} w={13} tone="cream" opacity={0.94} />
      <Shape x={19} y={10} w={20} tone="amber" />
      <Shape x={32} y={25} w={10} tone="copper" opacity={0.82} />
      <Line x={12} y={36} w={25} rotate={-10} />
    </>
  );
}

function IllustrationScene({ kind }: { kind: ProgramCardIllustrationKind }) {
  if (kind === "fertility-check-in") return <FertilityCheckIn />;
  if (kind === "genetic-screening") return <GeneticScreening />;
  if (kind === "full-assessment") return <FullAssessment />;
  if (kind === "her-optimization") return <HerOptimization />;
  if (kind === "his-optimization") return <HisOptimization />;
  if (kind === "couples-optimization") return <CouplesOptimization />;
  if (kind === "egg-freezing") return <EggFreezing />;
  if (kind === "embryo-freezing") return <EmbryoFreezing />;
  if (kind === "iui-cycle") return <IuiCycle />;
  if (kind === "ivf-cycle") return <IvfCycle />;
  if (kind === "fet-cycle") return <FetCycle />;
  return <GenericProgram />;
}

export default function ProgramCardIllustration({
  kind,
  size = 50,
}: {
  kind: ProgramCardIllustrationKind;
  size?: number;
}) {
  const scale = size / 52;

  return (
    <View
      style={{
        width: size,
        height: size,
        overflow: "visible",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View
        style={{
          width: 52,
          height: 52,
          overflow: "visible",
          transform: [{ scale }],
        }}
      >
        <IllustrationScene kind={kind} />
      </View>
    </View>
  );
}
